import { useTranslation } from "react-i18next";
import styles from "./InstallmentPlans.module.scss";
import { InstallmentBankTypes } from "../../../../api/types/products.types";
import { resolveMediaUrl } from "../../../../utils/mediaUrl";

interface Props {
  banks: InstallmentBankTypes[];
  /** Price of the selected variant, in the currency being shopped in. */
  price: number;
  currency: string;
}

/**
 * The instalment ("taksit") plans a product can be bought on, one line per
 * bank, sitting under the add-to-cart row on the product page.
 *
 * This is a side note next to the price, so every plan of every bank fits on a
 * single line and the whole block stays out of the way. Both the banks and
 * their plans come from the admin panel, so a shopper sees exactly what is on
 * offer today rather than a fixed list.
 */
const InstallmentPlans = ({ banks, price, currency }: Props) => {
  const { t } = useTranslation("translation");

  if (!banks.length || !price || price <= 0) return null;

  return (
    <div className={styles.installments}>
      <div className={styles.header}>
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
        </svg>
        <h3 className={styles.title}>{t("installments.title")}</h3>
      </div>

      <div className={styles.banks}>
        {banks.map((bank) => {
          const image = resolveMediaUrl(bank.image);

          return (
            <div key={bank.id} className={styles.bank}>
              <div className={styles.bank_head}>
                {image ? (
                  <img
                    src={image}
                    alt={bank.name}
                    className={styles.card_art}
                  />
                ) : (
                  // No card art uploaded yet, so draw one in the bank's colours
                  // rather than leaving a gap.
                  <div
                    className={styles.card_fallback}
                    style={{ background: bank.background_color }}
                  >
                    <span
                      className={styles.card_stripe}
                      style={{ background: bank.accent_color }}
                    />
                  </div>
                )}
                <span className={styles.bank_name}>{bank.name}</span>
              </div>

              <div className={styles.terms}>
                {bank.terms.map((term) => {
                  const total = price * (1 + term.markup_percent / 100);
                  const monthly = total / term.months;

                  return (
                    <div
                      key={term.id}
                      className={styles.term}
                      style={{ borderColor: bank.accent_color }}
                      title={
                        t("installments.total", {
                          amount: total.toFixed(2),
                          currency,
                        }) as string
                      }
                    >
                      <span className={styles.term_months}>
                        {t("installments.months", { months: term.months })}
                      </span>
                      <span className={styles.term_amount}>
                        {monthly.toFixed(2)} {currency}
                        <span className={styles.term_suffix}>
                          {t("installments.per_month_suffix")}
                        </span>
                      </span>
                      {term.markup_percent > 0 && (
                        <span className={styles.term_markup}>
                          +{term.markup_percent}%
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <p className={styles.note}>{t("installments.note")}</p>
    </div>
  );
};

export default InstallmentPlans;
